import React, { useCallback } from 'react';

import { Splice } from '@paragon/contract-library';
import { Direction2D, Point2D } from '@paragon/geometry-library';
import { newVector3 } from '@paragon/three-shared';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { nudgeReferenceLines } from '../../store/actions/editorActions';
import { getActiveComponentDesign } from '../../store/selectors/getActiveComponentDesignSelector';
import { calculateSpliceSegment, getEditableSpliceReferenceLines } from '../component/SpliceVisual';
import { NudgeHandle } from './NudgeHandle';

export function SpliceEditorHandle({ splice }: { splice: Splice }) {
  const dispatch = useAppDispatch();

  const { component, guid: componentDesignGuid } = useAppSelector(getActiveComponentDesign)!;

  const member1 = component.members.find(({ guid }) => guid === splice.neighbor1Guid);
  const member2 = component.members.find(({ guid }) => guid === splice.neighbor2Guid);

  const spliceSegment = member1 && member2 ? calculateSpliceSegment(member1, member2, component) : null;

  const editableSpliceReferenceLines =
    member1 && member2 && spliceSegment
      ? getEditableSpliceReferenceLines(member1, member2, component, spliceSegment)
      : null;

  const coordinateUpDirection = spliceSegment?.direction ?? null;
  const moveCallBack = useCallback(
    (shiftX: number, shiftY: number) => {
      if (!coordinateUpDirection || !editableSpliceReferenceLines) return;

      const distance = new Point2D(shiftX, shiftY).getCoordinate(
        Direction2D.fromObject(coordinateUpDirection).rotateQuarterTurn(),
      );
      const referenceLineNudges = editableSpliceReferenceLines.map(({ line, referenceLine }) => ({
        referenceLineGuid: referenceLine.guid,
        nudge: line.isOrientedWith(coordinateUpDirection) ? distance : -distance,
      }));
      dispatch(nudgeReferenceLines(referenceLineNudges, componentDesignGuid));
    },
    [componentDesignGuid, coordinateUpDirection, dispatch, editableSpliceReferenceLines],
  );

  if (!member1 || !member2 || !spliceSegment || !editableSpliceReferenceLines || !coordinateUpDirection) return null;

  return (
    <NudgeHandle
      editType="EditingSplice"
      leftRight
      moveCallback={moveCallBack}
      position={newVector3(spliceSegment.midpoint.to3D())}
      size={8}
      coordinateUpDirection={coordinateUpDirection}
      settingsButton={{ type: 'Snapping', position: { x: 9, y: 0, z: 0 }, size: 3 }}
    />
  );
}
